import { Link } from "react-router-dom";
import { ArrowLeft, Check, CircleDashed, Mail } from "lucide-react";
import { Header, SwissCross } from "@/components/Header";
import { SiteFooter } from "@/components/SiteFooter";
import { COMPANY } from "@/components/legal/LegalPage";
import { useLang } from "@/context/LangContext";
import { useDocTitle } from "@/context/useDocTitle";
import { securityGroups, ROADMAP, RIGHTS } from "@/components/security/securityContent";

const Measure = ({ m, i }) => (
  <li data-testid={`security-measure-${m.id}`} className="grid grid-cols-[2.5rem_1fr] gap-4 py-5 border-t border-[#d5dbde] first:border-t-0">
    <span className="font-mono text-xs text-[#E0262C] pt-1">{String(i + 1).padStart(2, "0")}</span>
    <div>
      <div className="flex items-center gap-2 font-display text-lg text-[#09090b]"><Check className="w-4 h-4 text-[#10b981]" /> {m.title}</div>
      <p className="mt-1 text-[15px] leading-relaxed text-[#3f454c] max-w-[62ch]">{m.text}</p>
    </div>
  </li>
);

const Group = ({ g, offset }) => (
  <section data-testid={`security-group-${g.id}`} className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 lg:gap-12 py-12 border-t-2 border-[#09090b]">
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-[#71717a]">{g.kicker}</div>
      <h2 className="mt-2 font-display text-2xl tracking-tight">{g.title}</h2>
    </div>
    <ol>{g.items.map((m, i) => <Measure key={m.id} m={m} i={offset + i} />)}</ol>
  </section>
);

const Hero = ({ tr }) => (
  <section className="max-w-6xl mx-auto w-full px-6 lg:px-10 pt-14 pb-10">
    <Link to="/" data-testid="security-back" className="inline-flex items-center gap-1.5 text-sm text-[#5b6168] hover:text-[#E0262C] transition-colors">
      <ArrowLeft className="w-4 h-4" /> {tr("Torna al sito", "Back to site")}
    </Link>
    <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10 items-end">
      <div>
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-mono text-[#E0262C]">
          <SwissCross className="w-3 h-3" /> {COMPANY.name} · Lugano
        </div>
        <h1 data-testid="security-title" className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
          {tr("Sicurezza", "Security")}<br />{tr("e privacy", "and privacy")}
        </h1>
        <p className="mt-6 max-w-[58ch] text-base text-[#3f454c] leading-relaxed">
          {tr("Lavoriamo con dati di aziende e persone reali. Qui trovi, senza giri di parole, le misure tecniche e organizzative che applichiamo oggi per proteggerli, e quelle che stiamo introducendo.",
            "We work with data about real companies and people. Here you will find, plainly stated, the technical and organisational measures we apply today to protect it, and those we are introducing.")}
        </p>
      </div>
      <div data-testid="security-compliance-badge" className="border-2 border-[#09090b] bg-white p-5">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717a]"><SwissCross className="w-3 h-3" /> nLPD · LPD 2023</div>
        <div className="mt-2 font-display text-2xl">{tr("Conforme alla nLPD", "nFADP compliant")}</div>
        <p className="mt-2 text-xs text-[#5b6168] leading-relaxed">
          {tr("Trattamento dei dati secondo la Legge federale sulla protezione dei dati (in vigore dal 1° settembre 2023) e, per gli utenti UE, il GDPR.",
            "Data processed under the Swiss Federal Act on Data Protection (in force since 1 September 2023) and, for EU users, the GDPR.")}
        </p>
      </div>
    </div>
  </section>
);

const Roadmap = ({ tr, items }) => (
  <section data-testid="security-roadmap" className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 lg:gap-12 py-12 border-t-2 border-[#09090b]">
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-[#71717a]">{tr("In arrivo", "Coming next")}</div>
      <h2 className="mt-2 font-display text-2xl tracking-tight">{tr("Prossimi passi", "Next steps")}</h2>
    </div>
    <ul className="grid sm:grid-cols-2 gap-px bg-[#d5dbde] border border-[#d5dbde]">
      {items.map((r) => (
        <li key={r.title} className="bg-[#f4f6f7] p-5">
          <div className="flex items-center gap-2 font-medium text-[#2B3036]"><CircleDashed className="w-4 h-4 text-[#9ca3af]" /> {r.title}</div>
          <p className="mt-1 text-sm text-[#5b6168]">{r.text}</p>
        </li>
      ))}
    </ul>
  </section>
);

const Rights = ({ tr, items }) => (
  <section data-testid="security-rights" className="bg-[#09090b] text-white">
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-[#E0262C]">{tr("I tuoi diritti", "Your rights")}</div>
        <h2 className="mt-2 font-display text-2xl tracking-tight">{tr("Hai il controllo dei tuoi dati", "You control your data")}</h2>
      </div>
      <div>
        <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-3 text-sm text-[#c4c9ce]">
          {items.map((r) => <li key={r} className="flex gap-2"><Check className="w-4 h-4 mt-0.5 text-[#E0262C] shrink-0" /> {r}</li>)}
        </ul>
        <a href={`mailto:${COMPANY.email}`} data-testid="security-contact" className="mt-8 inline-flex items-center gap-2 h-12 px-6 bg-[#E0262C] text-white font-medium hover:bg-white hover:text-[#09090b] transition-colors">
          <Mail className="w-4 h-4" /> {COMPANY.email}
        </a>
        <p className="mt-4 text-xs text-[#8b9096]">
          {tr("Per i dettagli su quali dati raccogliamo, per quanto tempo e con chi li condividiamo, leggi la", "For details on what data we collect, for how long and with whom we share it, read the")}{" "}
          <Link to="/privacy-policy" data-testid="security-privacy-link" className="underline hover:text-white">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  </section>
);

export default function Security() {
  const { lang, setLang, t, tr } = useLang();
  useDocTitle(tr("Sicurezza e privacy · Aurora-ADV", "Security and privacy · Aurora-ADV"),
    "Sicurezza e privacy AURORA-ADV: misure tecniche e organizzative in atto, conformità alla nLPD svizzera e al GDPR, diritti sui dati. PIXAWEB Sagl, Lugano.");
  const groups = securityGroups(tr);
  const offsets = groups.map((_, i) => groups.slice(0, i).reduce((a, g) => a + g.items.length, 0));
  return (
    <div data-testid="security-page" className="min-h-screen bg-[#E6EAEC] flex flex-col">
      <Header lang={lang} setLang={setLang} t={t} />
      <Hero tr={tr} />
      <main className="max-w-6xl mx-auto w-full px-6 lg:px-10 pb-16">
        {groups.map((g, i) => <Group key={g.id} g={g} offset={offsets[i]} />)}
        <Roadmap tr={tr} items={ROADMAP(tr)} />
      </main>
      <Rights tr={tr} items={RIGHTS(tr)} />
      <p className="max-w-6xl mx-auto px-6 lg:px-10 py-6 text-[11px] text-[#71717a]">
        {tr("Ultimo aggiornamento: giugno 2026. Questa pagina descrive le misure in atto e non costituisce una certificazione rilasciata da un ente terzo.",
          "Last updated: June 2026. This page describes the measures in place and is not a certification issued by a third party.")}
      </p>
      <div className="mt-auto"><SiteFooter /></div>
    </div>
  );
}
